/**
 * 実行環境の判定。
 *
 * ここでの判定は「UI の出し分け」と「診断結果の説明」にのみ使う。
 * 実際に何ができるかは lib/audioProbe.ts の実測に委ねる。
 */

export interface PlatformInfo {
  /** iOS / iPadOS (iPadOS 13+ は UA が Macintosh になるため maxTouchPoints で見分ける) */
  isIOS: boolean;
  /**
   * iPhone 相当の小画面 idiom。
   * WebKit の defaultVolumeLocked() が PLATFORM(IOS) かつ小画面 idiom のとき true を返し、
   * HTMLMediaElement.volume への代入が非同期に差し戻される。iPad は false。
   */
  isIPhoneLike: boolean;
  isSafari: boolean;
  /** ホーム画面から起動した standalone PWA か */
  isStandalone: boolean;
}

export function detectPlatform(): PlatformInfo {
  const ua = navigator.userAgent;
  const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || isIPadOS;
  const isIPhoneLike = /iPhone|iPod/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  const nav = navigator as Navigator & { standalone?: boolean };
  const isStandalone =
    nav.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

  return { isIOS, isIPhoneLike, isSafari, isStandalone };
}

export const platform = detectPlatform();

/**
 * 複数音声の同時再生が許されない前提で UI を組むべきか。
 *
 * 根拠: WebKit の MediaSessionManageriOS::resetRestrictions() が
 * MediaType::VideoAudio に ConcurrentPlaybackNotPermitted を付けている。
 * iOS/iPadOS 共通 (Mac Catalyst のみ例外)。
 *
 * ただし **この制限は再生開始時 (admission) にしか評価されず**、しかも
 * enforceConcurrentPlaybackRestriction() は currentSession() 以外では
 * 早期 return する。そのためミュートで再生を始めてから解除すると
 * 2 つ同時に鳴る (実機で確認済み。docs/feasibility.md §2.6)。
 *
 * それでも true のままにしてある。順番に依存し、どちらかで再生が始まり直すと
 * 片方が止まるため、機能として当てにできる挙動ではないから。
 */
export const assumeSingleAudio = platform.isIOS;
